import React, { useState, useEffect, useCallback } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axios from '../../axiosConfig';
import './TeamActivityLogs.css';
import {
  Activity,
  User,
  Clock,
  Filter,
  Search,
  RefreshCw,
  ShoppingCart,
  Package,
  DollarSign,
  LogIn,
  LogOut,
  TrendingUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Users,
  MapPin,
  X,
  Eye,
  Hash,
  FileText,
  ArrowRight
} from 'lucide-react';

// Activity type icons and labels
const ACTIVITY_CONFIG = {
  LOGIN: { icon: LogIn, label: 'Login', color: '#10b981' },
  LOGOUT: { icon: LogOut, label: 'Logout', color: '#6b7280' },
  LOGIN_FAILED: { icon: AlertCircle, label: 'Failed Login', color: '#ef4444' },
  POS_SALE: { icon: ShoppingCart, label: 'POS Sale', color: '#0d7463' },
  POS_REFUND: { icon: DollarSign, label: 'POS Refund', color: '#f59e0b' },
  POS_DISCOUNT_APPLIED: { icon: DollarSign, label: 'Discount Applied', color: '#8b5cf6' },
  POS_VOID: { icon: AlertCircle, label: 'Voided Order', color: '#ef4444' },
  POS_CUSTOM_ITEM: { icon: Package, label: 'Custom Item Added', color: '#8b5cf6' },
  POS_CUSTOM_ITEM_OVERRIDE: { icon: AlertCircle, label: 'Custom Item (Override)', color: '#f59e0b' },
  POS_SHIFT_START: { icon: Clock, label: 'Shift Started', color: '#10b981' },
  POS_SHIFT_END: { icon: Clock, label: 'Shift Ended', color: '#6b7280' },
  STOCK_ADJUSTED: { icon: Package, label: 'Stock Adjusted', color: '#3b82f6' },
  STOCK_TRANSFER_CREATED: { icon: Package, label: 'Stock Transfer Created', color: '#6366f1' },
  STOCK_TRANSFER_APPROVED: { icon: Package, label: 'Stock Transfer Approved', color: '#10b981' },
  STOCK_TRANSFER_REJECTED: { icon: Package, label: 'Stock Transfer Rejected', color: '#ef4444' },
  PRODUCT_CREATED: { icon: Package, label: 'Product Created', color: '#10b981' },
  PRODUCT_UPDATED: { icon: Package, label: 'Product Updated', color: '#3b82f6' },
  PRODUCT_DELETED: { icon: Package, label: 'Product Deleted', color: '#ef4444' },
  PRODUCT_PRICE_CHANGED: { icon: DollarSign, label: 'Price Changed', color: '#f59e0b' },
  ORDER_STATUS_CHANGED: { icon: ShoppingCart, label: 'Order Status Changed', color: '#3b82f6' },
  ORDER_REFUND_INITIATED: { icon: DollarSign, label: 'Refund Initiated', color: '#f59e0b' },
  CUSTOMER_CREATED: { icon: User, label: 'Customer Created', color: '#10b981' },
  CUSTOMER_UPDATED: { icon: User, label: 'Customer Updated', color: '#3b82f6' },
  SETTINGS_CHANGED: { icon: Activity, label: 'Settings Changed', color: '#6b7280' },
  CLOCK_IN: { icon: Clock, label: 'Clock In', color: '#10b981' },
  CLOCK_OUT: { icon: Clock, label: 'Clock Out', color: '#6b7280' },
  CUSTOM: { icon: Activity, label: 'Activity', color: '#6b7280' }
};

const TeamActivityLogs = () => {
  const { isAuthenticated } = useSecureAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    action: '',
    teamMemberId: '',
    period: 'today',
    startDate: '',
    endDate: ''
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('logs'); // 'logs' or 'summary'
  const [selectedLog, setSelectedLog] = useState(null); // For details modal

  // Fetch activity summary
  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', filters.period);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(`/team-activity/summary?${params}`);
      if (response.data.success) {
        setSummary(response.data.summary);
        setTeamMembers(response.data.summary.teamMembers || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity summary:', error);
    }
  }, [filters.period, filters.startDate, filters.endDate]);

  // Fetch activity logs
  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);

      if (filters.action) params.append('action', filters.action);
      if (filters.teamMemberId) params.append('teamMemberId', filters.teamMemberId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.period && !filters.startDate && !filters.endDate) {
        params.append('period', filters.period);
      }

      const response = await axios.get(`/team-activity/logs?${params}`);
      if (response.data.success) {
        setLogs(response.data.logs);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      fetchSummary();
      fetchLogs();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when filters change
  useEffect(() => {
    if (isAuthenticated) {
      fetchLogs(1);
      fetchSummary();
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Format date/time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  // Get activity config
  const getActivityConfig = (action) => {
    return ACTIVITY_CONFIG[action] || ACTIVITY_CONFIG.CUSTOM;
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchLogs(newPage);
    }
  };

  // Handle period filter change
  const handlePeriodChange = (period) => {
    setFilters(prev => ({ ...prev, period, startDate: '', endDate: '' }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      action: '',
      teamMemberId: '',
      period: 'today',
      startDate: '',
      endDate: ''
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
  };

  // Get log details for modal
  const getLogDetails = (log) => {
    const details = [];
    const meta = log.metadata || {};

    switch (log.action) {
      case 'POS_SALE':
        details.push({ label: 'Order Total', value: formatCurrency(meta.orderTotal) });
        details.push({ label: 'Items', value: meta.itemCount || 0 });
        break;
      case 'POS_REFUND':
        details.push({ label: 'Refund Amount', value: formatCurrency(meta.refundAmount) });
        details.push({ label: 'Reason', value: meta.reason || 'N/A' });
        break;
      case 'POS_CUSTOM_ITEM':
      case 'POS_CUSTOM_ITEM_OVERRIDE':
        details.push({ label: 'Item Name', value: meta.itemName || 'Custom Item' });
        details.push({ label: 'Price', value: formatCurrency(meta.itemPrice) });
        details.push({ label: 'Quantity', value: meta.itemQuantity || 1 });
        details.push({ label: 'Total', value: formatCurrency(meta.itemTotal) });
        if (meta.overrideRequired) {
          details.push({ label: 'Override By', value: meta.overrideBy || 'Manager' });
        }
        if (meta.description) {
          details.push({ label: 'Description', value: meta.description });
        }
        break;
      case 'POS_SHIFT_START':
        details.push({ label: 'Opening Cash', value: formatCurrency(meta.openingCash) });
        if (meta.locationId) {
          details.push({ label: 'Location', value: meta.locationId });
        }
        break;
      case 'POS_SHIFT_END':
        details.push({ label: 'Opening Cash', value: formatCurrency(meta.openingCash) });
        details.push({ label: 'Closing Cash', value: formatCurrency(meta.closingCash) });
        details.push({ label: 'Expected Cash', value: formatCurrency(meta.expectedCash) });
        details.push({
          label: 'Cash Variance',
          value: formatCurrency(meta.cashDifference),
          highlight: meta.cashDifference !== 0 ? (meta.cashDifference > 0 ? 'over' : 'short') : 'balanced'
        });
        details.push({ label: 'Total Sales', value: formatCurrency(meta.totalSales) });
        details.push({ label: 'Orders', value: meta.totalOrders || 0 });
        if (meta.refundCount > 0) {
          details.push({ label: 'Refunds', value: `${meta.refundCount} (${formatCurrency(meta.totalRefunds)})` });
        }
        if (meta.tipTotal > 0) {
          details.push({ label: 'Tips', value: formatCurrency(meta.tipTotal) });
        }
        if (meta.duration) {
          const hours = Math.floor(meta.duration / 60);
          const mins = meta.duration % 60;
          details.push({ label: 'Duration', value: `${hours}h ${mins}m` });
        }
        break;
      case 'STOCK_ADJUSTED':
        if (log.previousValue?.quantity !== undefined) {
          details.push({ label: 'Previous Qty', value: log.previousValue.quantity });
        }
        if (log.newValue?.quantity !== undefined) {
          details.push({ label: 'New Qty', value: log.newValue.quantity });
        }
        details.push({ label: 'Adjustment', value: meta.adjustment || 0 });
        if (meta.reason) {
          details.push({ label: 'Reason', value: meta.reason });
        }
        break;
      case 'PRODUCT_PRICE_CHANGED':
        if (log.previousValue?.price !== undefined) {
          details.push({ label: 'Previous Price', value: formatCurrency(log.previousValue.price) });
        }
        if (log.newValue?.price !== undefined) {
          details.push({ label: 'New Price', value: formatCurrency(log.newValue.price) });
        }
        break;
      case 'POS_DISCOUNT_APPLIED':
        details.push({ label: 'Discount Amount', value: formatCurrency(meta.discountAmount) });
        details.push({ label: 'Discount Type', value: meta.discountType || 'Manual' });
        break;
      default:
        // Show any metadata as details
        Object.entries(meta).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
            details.push({
              label,
              value: typeof value === 'number' && key.toLowerCase().includes('amount')
                ? formatCurrency(value)
                : String(value)
            });
          }
        });
    }

    return details;
  };

  return (
    <Shell>
      <FeatureGate featureId="team.activity_logs" fallback={
        <div className="feature-locked">
          <Activity size={48} />
          <h2>Team Activity Logs</h2>
          <p>Track all team member activities including logins, sales, and more.</p>
          <p className="upgrade-notice">This feature is available on PRO and PREMIUM plans.</p>
        </div>
      }>
        <div className="team-activity-container">
          {/* Header */}
          <div className="team-activity-header">
            <div className="header-left">
              <div className="header-icon">
                <Activity size={24} />
              </div>
              <div>
                <h1>Team Activity Logs</h1>
                <p>Monitor team member activities and performance</p>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={18} />
                Filters
              </button>
              <button
                className="refresh-btn"
                onClick={() => { fetchLogs(pagination.page); fetchSummary(); }}
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>

          {/* Period Selector */}
          <div className="period-selector">
            {['today', 'yesterday', 'week', 'month'].map(period => (
              <button
                key={period}
                className={`period-btn ${filters.period === period ? 'active' : ''}`}
                onClick={() => handlePeriodChange(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Activity Type</label>
                <select
                  value={filters.action}
                  onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                >
                  <option value="">All Activities</option>
                  {Object.entries(ACTIVITY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Team Member</label>
                <select
                  value={filters.teamMemberId}
                  onChange={(e) => setFilters(prev => ({ ...prev, teamMemberId: e.target.value }))}
                >
                  <option value="">All Members</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.username || member.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Date Range</label>
                <div className="date-range">
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value, period: '' }))}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value, period: '' }))}
                  />
                </div>
              </div>
              <button className="clear-filters" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          )}

          {/* Summary Cards */}
          {summary && (
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-icon" style={{ background: '#0d7463' }}>
                  <Activity size={20} />
                </div>
                <div className="card-content">
                  <span className="card-value">{summary.totalActivities}</span>
                  <span className="card-label">Total Activities</span>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon" style={{ background: '#3b82f6' }}>
                  <Users size={20} />
                </div>
                <div className="card-content">
                  <span className="card-value">{teamMembers.length}</span>
                  <span className="card-label">Active Members</span>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon" style={{ background: '#10b981' }}>
                  <ShoppingCart size={20} />
                </div>
                <div className="card-content">
                  <span className="card-value">
                    {summary.activityBreakdown?.find(a => a.action === 'POS_SALE')?.count || 0}
                  </span>
                  <span className="card-label">POS Sales</span>
                </div>
              </div>
              <div className="summary-card">
                <div className="card-icon" style={{ background: '#8b5cf6' }}>
                  <LogIn size={20} />
                </div>
                <div className="card-content">
                  <span className="card-value">
                    {summary.activityBreakdown?.find(a => a.action === 'LOGIN')?.count || 0}
                  </span>
                  <span className="card-label">Logins</span>
                </div>
              </div>
            </div>
          )}

          {/* Tab Selector */}
          <div className="tab-selector">
            <button
              className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Activity Log
            </button>
            <button
              className={`tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              Team Summary
            </button>
          </div>

          {/* Activity Log Tab */}
          {activeTab === 'logs' && (
            <div className="activity-logs">
              {loading ? (
                <div className="loading-state">
                  <RefreshCw className="spin" size={24} />
                  <p>Loading activities...</p>
                </div>
              ) : logs.length === 0 ? (
                <div className="empty-state">
                  <Activity size={48} />
                  <h3>No activities found</h3>
                  <p>Team activities will appear here as they happen.</p>
                </div>
              ) : (
                <>
                  <div className="logs-list">
                    {logs.map(log => {
                      const config = getActivityConfig(log.action);
                      const IconComponent = config.icon;
                      return (
                        <div
                          key={log.id}
                          className="log-item clickable"
                          onClick={() => setSelectedLog(log)}
                        >
                          <div className="log-icon" style={{ background: `${config.color}15`, color: config.color }}>
                            <IconComponent size={18} />
                          </div>
                          <div className="log-content">
                            <div className="log-main">
                              <span className="log-performer">
                                {log.teamMember?.user?.username || log.teamMember?.user?.email || 'Unknown'}
                              </span>
                              <span className="log-action" style={{ color: config.color }}>
                                {config.label}
                              </span>
                              {log.targetType && (
                                <span className="log-target">
                                  {log.targetType}: {log.targetId?.slice(0, 8)}...
                                </span>
                              )}
                            </div>
                            <div className="log-meta">
                              <span className="log-role">{log.teamMember?.role?.name || 'Team Member'}</span>
                              <span className="log-time">
                                <Clock size={12} />
                                {formatRelativeTime(log.createdAt)}
                              </span>
                              {log.metadata?.orderTotal && (
                                <span className="log-amount">
                                  <DollarSign size={12} />
                                  {formatCurrency(log.metadata.orderTotal)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="log-view-btn">
                            <Eye size={16} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <span>
                        Page {pagination.page} of {pagination.pages}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Team Summary Tab */}
          {activeTab === 'summary' && (
            <div className="team-summary">
              {teamMembers.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} />
                  <h3>No team members</h3>
                  <p>Add team members to see their activity summary.</p>
                </div>
              ) : (
                <div className="members-grid">
                  {teamMembers.map(member => (
                    <div key={member.id} className="member-card">
                      <div className="member-avatar">
                        {(member.username || member.email || 'U')[0].toUpperCase()}
                      </div>
                      <div className="member-info">
                        <h4>{member.username || member.email?.split('@')[0]}</h4>
                        <span className="member-role">{member.role || 'Team Member'}</span>
                      </div>
                      <div className="member-stats">
                        <div className="stat">
                          <span className="stat-value">{member.activityCount}</span>
                          <span className="stat-label">Activities</span>
                        </div>
                      </div>
                      {member.lastActiveAt && (
                        <div className="member-last-active">
                          <Clock size={12} />
                          Last active: {formatRelativeTime(member.lastActiveAt)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Log Details Modal */}
          {selectedLog && (
            <div className="log-modal-overlay" onClick={() => setSelectedLog(null)}>
              <div className="log-modal" onClick={e => e.stopPropagation()}>
                <div className="log-modal-header">
                  <div className="modal-header-left">
                    {(() => {
                      const config = getActivityConfig(selectedLog.action);
                      const IconComponent = config.icon;
                      return (
                        <div className="modal-icon" style={{ background: `${config.color}15`, color: config.color }}>
                          <IconComponent size={24} />
                        </div>
                      );
                    })()}
                    <div>
                      <h2>{getActivityConfig(selectedLog.action).label}</h2>
                      <p className="modal-subtitle">
                        By {selectedLog.teamMember?.user?.username || selectedLog.teamMember?.user?.email || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <button className="modal-close" onClick={() => setSelectedLog(null)}>
                    <X size={20} />
                  </button>
                </div>

                <div className="log-modal-body">
                  {/* Basic Info */}
                  <div className="modal-section">
                    <h3>Activity Information</h3>
                    <div className="modal-info-grid">
                      <div className="info-item">
                        <span className="info-label"><Clock size={14} /> Time</span>
                        <span className="info-value">{formatDateTime(selectedLog.createdAt)}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label"><User size={14} /> Performer</span>
                        <span className="info-value">
                          {selectedLog.teamMember?.user?.username || selectedLog.teamMember?.user?.email}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label"><Users size={14} /> Role</span>
                        <span className="info-value">{selectedLog.teamMember?.role?.name || 'Team Member'}</span>
                      </div>
                      {selectedLog.targetType && (
                        <div className="info-item">
                          <span className="info-label"><FileText size={14} /> Target</span>
                          <span className="info-value">{selectedLog.targetType}: {selectedLog.targetId}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Activity Details */}
                  {getLogDetails(selectedLog).length > 0 && (
                    <div className="modal-section">
                      <h3>Details</h3>
                      <div className="modal-details-list">
                        {getLogDetails(selectedLog).map((detail, idx) => (
                          <div
                            key={idx}
                            className={`detail-row ${detail.highlight ? `highlight-${detail.highlight}` : ''}`}
                          >
                            <span className="detail-label">{detail.label}</span>
                            <span className="detail-value">{detail.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Previous/New Value (for changes) */}
                  {(selectedLog.previousValue || selectedLog.newValue) && (
                    <div className="modal-section">
                      <h3>Value Changes</h3>
                      <div className="value-changes">
                        {selectedLog.previousValue && (
                          <div className="value-box previous">
                            <span className="value-label">Previous</span>
                            <pre>{JSON.stringify(selectedLog.previousValue, null, 2)}</pre>
                          </div>
                        )}
                        {selectedLog.previousValue && selectedLog.newValue && (
                          <ArrowRight size={20} className="change-arrow" />
                        )}
                        {selectedLog.newValue && (
                          <div className="value-box new">
                            <span className="value-label">New</span>
                            <pre>{JSON.stringify(selectedLog.newValue, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Technical Info */}
                  <div className="modal-section technical">
                    <h3>Technical Info</h3>
                    <div className="technical-info">
                      <div className="tech-row">
                        <span className="tech-label"><Hash size={12} /> Log ID</span>
                        <span className="tech-value">{selectedLog.id}</span>
                      </div>
                      {selectedLog.ipAddress && (
                        <div className="tech-row">
                          <span className="tech-label"><MapPin size={12} /> IP Address</span>
                          <span className="tech-value">{selectedLog.ipAddress}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
};

export default TeamActivityLogs;
